import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent, NzFormLabelComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberComponent } from 'ng-zorro-antd/input-number';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTableModule } from 'ng-zorro-antd/table';
import { AuthService } from '../services/auth.service';
import { BillingService } from '../services/billing.service';
import { UpgradeContractModalComponent } from '../billing/upgrade-contract-modal.component';
import { TeamAccessService } from '../services/team-access.service';
import {
  buildRoleCapabilitySummary,
  roleDescriptionKey,
  roleNameKey,
  RoleCapabilitySummary,
  RoleDefaultsMap
} from '../utils/role-capabilities.utils';
import { isSeatLimitError, shouldSuppressGenericError } from '../utils/http-error.utils';

@Component({
  selector: 'app-account-settings-team-access',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NzAlertComponent,
    NzCardModule,
    NzCheckboxModule,
    NzTableModule,
    NzButtonModule,
    NzInputModule,
    NzInputNumberComponent,
    NzSelectModule,
    NzSwitchModule,
    NzModalModule,
    NzFormControlComponent,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzFormDirective,
    NzRowDirective,
    NzColDirective,
  ],
  templateUrl: './account-settings-team-access.component.html',
  styleUrl: './account-settings-team-access.component.less'
})
export class AccountSettingsTeamAccessComponent implements OnInit {
  users: any[] = [];
  invites: any[] = [];
  seatInfo: any = null;
  loading = false;

  inviteEmail = '';
  inviteDisplayName = '';
  inviteCompanyRole = 'viewer';
  roleDefaults: RoleDefaultsMap = {};
  selectedRoleInfo: string = 'viewer';

  roleOptions = ['company_admin', 'editor', 'viewer'];
  editUserModalVisible = false;
  editingUser: any = null;
  editDisplayName = '';
  editCompanyRole = 'viewer';

  increaseSeatLimitModalVisible = false;
  increaseSeatLimitSubmitting = false;
  increaseSeatLimitForm!: FormGroup;
  seatLimitIncreaseValidationMessage: string | null = null;
  pricePerAdditionalUserNetYear: number | null = null;
  baseIncludedObjects: number | null = null;
  basePackagePriceNetYear: number | null = null;
  upgradeSettingsCurrency: string = 'EUR';
  pendingUpgrade: any = null;

  constructor(
    private authService: AuthService,
    private teamAccessService: TeamAccessService,
    private billingService: BillingService,
    private message: NzMessageService,
    private modal: NzModalService,
    private translate: TranslateService,
    private fb: FormBuilder,
  ) {
    this.increaseSeatLimitForm = this.fb.group({
      newSeatLimit: [null, [Validators.required, this.seatLimitIncreaseValidator.bind(this)]],
      confirmedImmediateInvoice: [false, [Validators.requiredTrue]],
    });
  }

  ngOnInit(): void {
    this.loadRoleDefaults();
    this.load();
    void this.loadUpgradeSettingsIfAdmin();
  }

  private loadUpgradeSettingsIfAdmin(): Promise<void> {
    if (this.authService.getCurrentCompanyRole() !== 'company_admin') {
      this.pricePerAdditionalUserNetYear = null;
      this.baseIncludedObjects = null;
      this.basePackagePriceNetYear = null;
      this.pendingUpgrade = null;
      return Promise.resolve();
    }
    return this.loadUpgradeSettings();
  }

  private loadUpgradeSettings(): Promise<void> {
    return new Promise((resolve) => {
      this.billingService.getUpgradeSettings().subscribe({
        next: (settings) => {
          this.pricePerAdditionalUserNetYear = Number.isFinite(Number(settings?.pricePerAdditionalUserNetYear))
            ? Number(settings.pricePerAdditionalUserNetYear) : null;
          this.baseIncludedObjects = Number.isFinite(Number(settings?.baseIncludedObjects))
            ? Number(settings.baseIncludedObjects) : null;
          this.basePackagePriceNetYear = Number.isFinite(Number(settings?.basePackagePriceNetYear))
            ? Number(settings.basePackagePriceNetYear) : null;
          this.upgradeSettingsCurrency = String(settings?.currency || 'EUR');
          this.pendingUpgrade = settings?.pendingUpgrade || null;
          resolve();
        },
        error: () => {
          this.pricePerAdditionalUserNetYear = null;
          this.baseIncludedObjects = null;
          this.basePackagePriceNetYear = null;
          this.pendingUpgrade = null;
          resolve();
        },
      });
    });
  }

  get effectiveSeatLimit(): number | null {
    const limit = this.seatInfo?.effectiveSeatLimit;
    return (limit === null || typeof limit === 'undefined') ? null : Number(limit);
  }

  get usedSeatsCount(): number {
    return Number(this.seatInfo?.seatsUsed || 0);
  }

  get remainingSeatsCount(): number | null {
    const limit = this.effectiveSeatLimit;
    if (limit === null) return null;
    return Math.max(limit - this.usedSeatsCount, 0);
  }

  get isPaidCompanyPlan(): boolean {
    const plan = String(this.seatInfo?.plan || '').toLowerCase();
    return ['pro', 'business'].includes(plan);
  }

  get canManageSeatLimit(): boolean {
    return this.authService.getCurrentCompanyRole() === 'company_admin' && this.effectiveSeatLimit !== null;
  }

  get seatLimitIncreaseMin(): number {
    return Math.max((this.effectiveSeatLimit ?? 0) + 1, this.usedSeatsCount);
  }

  canInvite(): boolean {
    return this.authService.hasPermission('company.users.invite');
  }

  noSeatsAvailable(): boolean {
    const limit = this.seatInfo?.effectiveSeatLimit;
    if (limit === null || typeof limit === 'undefined') {
      return false;
    }
    const used = Number(this.seatInfo?.seatsUsed || 0);
    const pending = Number(this.seatInfo?.pendingInvites || 0);
    return (used + pending) >= Number(limit);
  }

  canUpdate(): boolean {
    return this.authService.hasPermission('company.users.update');
  }

  canDeactivate(): boolean {
    return this.authService.hasPermission('company.users.deactivate');
  }

  canRemove(): boolean {
    return this.authService.hasPermission('company.users.remove');
  }

  load(): void {
    this.loading = true;
    this.teamAccessService.listUsers().subscribe({
      next: (response) => {
        this.users = response?.users || [];
        this.seatInfo = response?.seatInfo || null;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });

    this.teamAccessService.listInvites().subscribe({
      next: (response) => {
        this.invites = Array.isArray(response) ? response : [];
      },
      error: () => {
        this.invites = [];
      }
    });
  }

  private loadRoleDefaults(): void {
    this.authService.getMe().subscribe({
      next: (me) => {
        this.roleDefaults = (me?.roleDefaults || {}) as RoleDefaultsMap;
      },
      error: () => {
        this.roleDefaults = {};
      },
    });
  }

  roleLabelKey(role: string): string {
    return roleNameKey(role);
  }

  roleDescription(role: string): string {
    return this.translate.instant(roleDescriptionKey(role));
  }

  getRoleCanLabels(role: string): string[] {
    const summary: RoleCapabilitySummary = buildRoleCapabilitySummary(this.roleDefaults, role, 8, 3);
    return summary.can.map((key: string) => this.translate.instant(key));
  }

  getRoleCannotLabels(role: string): string[] {
    const summary: RoleCapabilitySummary = buildRoleCapabilitySummary(this.roleDefaults, role, 8, 3);
    return summary.cannot.map((key: string) => this.translate.instant(key));
  }

  openIncreaseSeatLimitModal(): void {
    if (!this.canManageSeatLimit) {
      return;
    }

    if (!this.isPaidCompanyPlan) {
      if (this.pendingUpgrade?.contractStartDate) {
        const date = new Date(this.pendingUpgrade.contractStartDate);
        const dateStr = Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString(
          this.translate.currentLang === 'de' ? 'de-DE' : 'en-US',
          { day: '2-digit', month: '2-digit', year: 'numeric' }
        );
        this.message.info(this.translate.instant('TEAM_ACCESS.INCREASE_LIMIT.PENDING_UPGRADE', { date: dateStr }));
        return;
      }
      this.openUpgradeContractDraftModalForSeats();
      return;
    }

    const suggestedValue = Math.max((this.effectiveSeatLimit ?? 0) + 1, this.usedSeatsCount + 1);
    this.increaseSeatLimitForm.reset({
      newSeatLimit: suggestedValue,
      confirmedImmediateInvoice: false,
    });
    this.seatLimitIncreaseValidationMessage = null;
    this.increaseSeatLimitModalVisible = true;
  }

  closeIncreaseSeatLimitModal(): void {
    if (this.increaseSeatLimitSubmitting) {
      return;
    }
    this.increaseSeatLimitModalVisible = false;
    this.seatLimitIncreaseValidationMessage = null;
  }

  submitIncreaseSeatLimit(): void {
    if (this.increaseSeatLimitSubmitting) {
      return;
    }

    this.increaseSeatLimitForm.markAllAsTouched();
    if (this.increaseSeatLimitForm.invalid || this.seatLimitIncreaseValidationMessage) {
      return;
    }

    const newSeatLimit = Number(this.increaseSeatLimitForm.value.newSeatLimit);
    this.increaseSeatLimitSubmitting = true;
    this.billingService.increaseMySeatLimit(newSeatLimit)
      .pipe(finalize(() => {
        this.increaseSeatLimitSubmitting = false;
      }))
      .subscribe({
        next: (response) => {
          this.message.success(this.translate.instant('TEAM_ACCESS.INCREASE_LIMIT.SUCCESS'));
          this.increaseSeatLimitModalVisible = false;
          this.load();
        },
        error: (error) => {
          if (error?.code === 'UPGRADE_REQUIRED') {
            this.openUpgradeContractDraftModalForSeats();
            return;
          }
          this.message.error(this.resolveSeatLimitErrorMessage(error));
        }
      });
  }

  private resolveSeatLimitErrorMessage(error: any): string {
    switch (error?.code) {
      case 'COMPANY_ADMIN_REQUIRED':
        return this.translate.instant('TEAM_ACCESS.INCREASE_LIMIT.ERROR_COMPANY_ADMIN_REQUIRED');
      case 'SEAT_LIMIT_MUST_BE_POSITIVE_INTEGER':
        return this.translate.instant('TEAM_ACCESS.INCREASE_LIMIT.ERROR_WHOLE_NUMBER');
      case 'SEAT_LIMIT_MUST_EXCEED_CURRENT':
        return this.translate.instant('TEAM_ACCESS.INCREASE_LIMIT.ERROR_MUST_EXCEED_CURRENT');
      case 'SEAT_LIMIT_BELOW_USED':
        return this.translate.instant('TEAM_ACCESS.INCREASE_LIMIT.ERROR_BELOW_USED');
      default:
        return error?.message || this.translate.instant('TEAM_ACCESS.INCREASE_LIMIT.ERROR_SUBMIT');
    }
  }

  private positiveIntegerValidator(control: AbstractControl): ValidationErrors | null {
    if (control.value === null || control.value === '' || typeof control.value === 'undefined') {
      return null;
    }
    const value = Number(control.value);
    if (!Number.isFinite(value) || !Number.isInteger(value) || value <= 0) {
      return { notPositiveInteger: true };
    }
    return null;
  }

  private seatLimitIncreaseValidator(control: AbstractControl): ValidationErrors | null {
    const positiveIntegerError = this.positiveIntegerValidator(control);
    if (positiveIntegerError) {
      return positiveIntegerError;
    }

    const value = Number(control.value);
    if (!Number.isFinite(value) || control.value === null || control.value === '') {
      return null;
    }

    if (this.effectiveSeatLimit !== null && value <= this.effectiveSeatLimit) {
      return { mustExceedCurrent: true };
    }

    if (value < this.usedSeatsCount) {
      return { belowUsed: true };
    }

    return null;
  }

  getSeatLimitIncreaseErrorMessage(): string | undefined {
    const control = this.increaseSeatLimitForm?.get('newSeatLimit');
    if (!control || !control.errors || !control.touched) {
      return undefined;
    }

    if (control.errors['required']) {
      return this.translate.instant('TEAM_ACCESS.INCREASE_LIMIT.ERROR_REQUIRED');
    }
    if (control.errors['notPositiveInteger']) {
      return this.translate.instant('TEAM_ACCESS.INCREASE_LIMIT.ERROR_WHOLE_NUMBER');
    }
    if (control.errors['mustExceedCurrent']) {
      return this.translate.instant('TEAM_ACCESS.INCREASE_LIMIT.ERROR_MUST_EXCEED_CURRENT');
    }
    if (control.errors['belowUsed']) {
      return this.translate.instant('TEAM_ACCESS.INCREASE_LIMIT.ERROR_BELOW_USED');
    }

    return undefined;
  }

  getSeatLimitIncreaseConfirmationErrorMessage(): string | undefined {
    const control = this.increaseSeatLimitForm?.get('confirmedImmediateInvoice');
    if (!control || !control.touched) {
      return undefined;
    }

    if (control.errors?.['requiredTrue']) {
      return this.translate.instant('TEAM_ACCESS.INCREASE_LIMIT.ERROR_CONFIRM_REQUIRED');
    }

    return undefined;
  }

  formatMoney(value: number | null | undefined): string {
    const amount = Number(value || 0);
    const lang = this.translate.currentLang || this.translate.defaultLang || 'de';
    const locale = lang.startsWith('de') ? 'de-DE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  private openUpgradeContractDraftModalForSeats(): void {
    const modalRef = this.modal.create({
      nzContent: UpgradeContractModalComponent,
      nzTitle: this.translate.instant('UPGRADE_CONTRACT_MODAL.TITLE'),
      nzFooter: null,
      nzWidth: 860,
      nzClosable: false,
      nzMaskClosable: false,
      nzData: {
        source: 'seats',
        suggestedAdditionalUsersCount: 1,
      },
    });

    modalRef.afterClose.subscribe((result) => {
      if (result?.signed) {
        if (result.pendingActivation) {
          this.pendingUpgrade = {
            plan: result.pendingPlan || result.plan || this.pendingUpgrade?.plan || null,
            maxProperties: result.pendingMaxProperties ?? this.pendingUpgrade?.maxProperties ?? null,
            seatLimit: result.pendingSeatLimit ?? this.pendingUpgrade?.seatLimit ?? null,
            contractStartDate: result.draft?.contractStartDate || this.pendingUpgrade?.contractStartDate || null,
            billingDocumentId: result.billingDocumentId || null,
          };
        } else if (this.seatInfo) {
          this.seatInfo = {
            ...this.seatInfo,
            plan: result.plan || this.seatInfo.plan,
            seatLimit: typeof result.seatLimit === 'number' ? result.seatLimit : this.seatInfo.seatLimit,
            effectiveSeatLimit: typeof result.seatLimit === 'number' ? result.seatLimit : this.seatInfo.effectiveSeatLimit,
          };
          this.pendingUpgrade = null;
        }

        this.load();
        this.loadRoleDefaults();
        void this.loadUpgradeSettings();
      }
    });
  }

  createInvite(): void {
    if (!this.inviteEmail.trim() || this.noSeatsAvailable()) {
      return;
    }

    this.teamAccessService.createInvite({
      email: this.inviteEmail.trim(),
      displayName: this.inviteDisplayName.trim() || undefined,
      companyRole: this.inviteCompanyRole,
      permissions: [],
    }).subscribe({
      next: () => {
        this.message.success(this.translate.instant('TEAM_ACCESS.INVITE_SENT'));
        this.inviteEmail = '';
        this.inviteDisplayName = '';
        this.load();
      },
      error: (error) => {
        if (shouldSuppressGenericError(error) || isSeatLimitError(error)) {
          return;
        }
        this.message.error(this.translate.instant('ERRORS.NOT_AUTHORIZED'));
      }
    });
  }

  updateRole(user: any, companyRole: string): void {
    this.teamAccessService.updateUser(user._id, { companyRole }).subscribe({
      next: () => {
        this.message.success(this.translate.instant('TEAM_ACCESS.USER_UPDATED'));
        this.load();
      },
      error: (error) => {
        if (shouldSuppressGenericError(error)) {
          return;
        }
        const key = error?.error?.messageKey || error?.messageKey;
        if (key === 'errors.lastCompanyAdminProtection') {
          this.message.error(this.translate.instant('ERRORS.LAST_COMPANY_ADMIN_PROTECTION'));
          return;
        }
        this.message.error(this.translate.instant('ERRORS.NOT_AUTHORIZED'));
      }
    });
  }

  openEditUserModal(user: any): void {
    this.editingUser = user;
    this.editDisplayName = user?.displayName || '';
    this.editCompanyRole = user?.companyRole || 'viewer';
    this.editUserModalVisible = true;
  }

  closeEditUserModal(): void {
    this.editUserModalVisible = false;
    this.editingUser = null;
    this.editDisplayName = '';
    this.editCompanyRole = 'viewer';
  }

  saveUserEdits(): void {
    if (!this.editingUser?._id) {
      return;
    }

    this.teamAccessService.updateUser(this.editingUser._id, {
      displayName: this.editDisplayName.trim(),
      companyRole: this.editCompanyRole,
    }).subscribe({
      next: () => {
        this.message.success(this.translate.instant('TEAM_ACCESS.USER_UPDATED'));
        this.closeEditUserModal();
        this.load();
      },
      error: (error) => {
        if (shouldSuppressGenericError(error)) {
          return;
        }
        const key = error?.error?.messageKey || error?.messageKey;
        if (key === 'errors.lastCompanyAdminProtection') {
          this.message.error(this.translate.instant('ERRORS.LAST_COMPANY_ADMIN_PROTECTION'));
          return;
        }
        this.message.error(this.translate.instant('ERRORS.NOT_AUTHORIZED'));
      }
    });
  }

  toggleDeactivation(user: any): void {
    this.teamAccessService.updateUserDeactivation(user._id, !user.isDeactivated).subscribe({
      next: () => this.load(),
      error: (error) => {
        if (shouldSuppressGenericError(error) || isSeatLimitError(error)) {
          return;
        }
        const key = error?.error?.messageKey || error?.messageKey;
        if (key === 'errors.lastCompanyAdminProtection') {
          this.message.error(this.translate.instant('ERRORS.LAST_COMPANY_ADMIN_PROTECTION'));
          return;
        }
        this.message.error(this.translate.instant('ERRORS.NOT_AUTHORIZED'));
      }
    });
  }

  removeUser(user: any): void {
    this.teamAccessService.removeUser(user._id).subscribe({
      next: () => this.load(),
      error: (error) => {
        if (shouldSuppressGenericError(error)) {
          return;
        }
        const key = error?.error?.messageKey || error?.messageKey;
        if (key === 'errors.lastCompanyAdminProtection') {
          this.message.error(this.translate.instant('ERRORS.LAST_COMPANY_ADMIN_PROTECTION'));
          return;
        }
        this.message.error(this.translate.instant('ERRORS.NOT_AUTHORIZED'));
      }
    });
  }

  revokeInvite(invite: any): void {
    this.teamAccessService.revokeInvite(invite._id).subscribe({
      next: () => this.load(),
      error: (error) => {
        if (shouldSuppressGenericError(error)) {
          return;
        }
        this.message.error(this.translate.instant('ERRORS.NOT_AUTHORIZED'));
      },
    });
  }

  inviteStatusKey(status: string): string {
    const normalized = typeof status === 'string' ? status.toUpperCase() : 'PENDING';
    if (['PENDING', 'ACCEPTED', 'REVOKED', 'EXPIRED'].includes(normalized)) {
      return `INVITES.STATUS.${normalized}`;
    }
    return 'INVITES.STATUS.PENDING';
  }
}
